import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';
import UsuariosServiceAPI, { Usuario as UsuarioType } from '../services/usuariosServiceAPI';
import { SolicitacoesAcessoServiceAPI, SolicitacaoAcesso } from '../services/solicitacoesAcessoServiceAPI';
import { NotificacoesService } from '../services/notificacoesService';
import { PerfisService, Perfil, PerfilPermissoes } from '../services/perfisService';
import FiliaisServiceAPI, { Filial } from '../services/filiaisServiceAPI';
import EditarUsuarioModal from '../components/common/EditarUsuarioModal';
import AlterarSenhaModal from '../components/common/AlterarSenhaModal';
import PerfilPermissoesModal from '../components/common/PerfilPermissoesModal';
import Modal from '../components/common/Modal';
import { usePermissoes } from '../hooks/usePermissoes';

// Tipo para Usuário da UI
type Usuario = {
  id: string;
  nome: string;
  login: string;
  email: string;
  perfil: string;
  filiais: string[];
  telefone?: string;
  cnpj?: string;
  regiao?: string;
  eh_transportadora?: boolean;
  primeiro_acesso: boolean;
  foto_perfil?: string;
  criado_em: string;
  bloqueado?: boolean;
  tipoBloqueio?: string;
  motivoBloqueio?: string;
};

const Usuarios: React.FC = () => {
  const { user } = useAuth();
  const { filialAtual } = useApp();
  const [abaAtiva, setAbaAtiva] = useState<'usuarios' | 'aprovacoes' | 'perfis'>('usuarios');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [usuariosFiltrados, setUsuariosFiltrados] = useState<Usuario[]>([]);
  const [solicitacoes, setSolicitacoes] = useState<SolicitacaoAcesso[]>([]);
  const [perfis, setPerfis] = useState<Perfil[]>([]);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroNome, setFiltroNome] = useState('');
  const [filtroPerfil, setFiltroPerfil] = useState('');
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Usuario | null>(null);
  const [modalEditOpen, setModalEditOpen] = useState(false);
  const [modalSenhaOpen, setModalSenhaOpen] = useState(false);
  const [solicitacaoSelecionada, setSolicitacaoSelecionada] = useState<SolicitacaoAcesso | null>(null);
  const [modalAprovarOpen, setModalAprovarOpen] = useState(false);
  const [modalReprovarOpen, setModalReprovarOpen] = useState(false);
  const [motivoReprovacao, setMotivoReprovacao] = useState('');
  const [perfilSelecionado, setPerfilSelecionado] = useState<Perfil | null>(null);
  const [modalPerfilOpen, setModalPerfilOpen] = useState(false);
  const [modalUsuarioOpen, setModalUsuarioOpen] = useState(false);
  const [perfilUsuarioModal, setPerfilUsuarioModal] = useState<string>('');

  const { temPermissao: podeAprovar } = usePermissoes('usuarios', 'aprovar');
  const { temPermissao: podeGerenciarPerfis } = usePermissoes('usuarios', 'gerenciar_perfis');

  const handleSalvarUsuario = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    try {
      const formData = new FormData(e.currentTarget);
      const perfilSelecionado = formData.get('perfil') as string;
      
      // Obter filiais selecionadas
      const filiaisSelecionadas: string[] = [];
      if (formData.get('filial_FILIAL_1') === 'on') filiaisSelecionadas.push('FILIAL_1');
      if (formData.get('filial_FILIAL_2') === 'on') filiaisSelecionadas.push('FILIAL_2');
      if (formData.get('filial_FILIAL_3') === 'on') filiaisSelecionadas.push('FILIAL_3');
      
      const usuarioData: any = {
        nome: formData.get('nome') as string,
        login: formData.get('login') as string,
        email: formData.get('email') as string,
        perfil_id: perfilSelecionado,
        telefone: formData.get('telefone') as string,
        filiais_permitidas: filiaisSelecionadas.length > 0 ? filiaisSelecionadas : ['FILIAL_1'],
        eh_transportadora: false,
        regiao: '',
        primeiro_acesso: !usuarioSelecionado, // Novos usuários precisam mudar senha
      };

      // Só incluir CNPJ se o perfil for fornecedor e o campo não estiver vazio
      const perfilObj = perfis.find(p => p.id === perfilSelecionado);
      const isFornecedor = perfilObj?.nome.toLowerCase() === 'fornecedor';
      if (isFornecedor) {
        const cnpj = formData.get('cnpj') as string;
        if (cnpj && cnpj.trim() !== '') {
          usuarioData.cnpj = cnpj;
        }
      }

      if (usuarioSelecionado) {
        // Editar usuário existente (sem senha)
        await UsuariosServiceAPI.atualizar(usuarioSelecionado.id, usuarioData);
      } else {
        // Criar novo usuário (com senha padrão)
        const usuarioComSenha = {
          ...usuarioData,
          senha: 'Up123456@Up123', // Senha padrão para novos usuários (atende requisitos de força)
          ativo: true
        };
        await UsuariosServiceAPI.criar(usuarioComSenha);
      }

      // Recarregar lista de usuários
      await carregarUsuarios();
      
      // Fechar modal
      setModalUsuarioOpen(false);
      setUsuarioSelecionado(null);
      
      alert(usuarioSelecionado ? 'Usuário atualizado com sucesso!' : 'Usuário criado com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar usuário:', error);
      const mensagem = error instanceof Error ? error.message : 'Erro ao salvar usuário. Verifique os dados e tente novamente.';
      alert('Erro ao salvar usuário: ' + mensagem);
    }
  };

  useEffect(() => {
    carregarUsuarios();
    carregarPerfis();
    carregarFiliais();
  }, []);

  useEffect(() => {
    if (podeAprovar) carregarSolicitacoes();
  }, [podeAprovar]);

  useEffect(() => {
    if (abaAtiva === 'aprovacoes' && !podeAprovar) setAbaAtiva('usuarios');
    if (abaAtiva === 'perfis' && !podeGerenciarPerfis) setAbaAtiva('usuarios');
  }, [podeAprovar, podeGerenciarPerfis, abaAtiva]);

  useEffect(() => {
    aplicarFiltros();
  }, [usuarios, filtroNome, filtroPerfil, filialAtual]);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      const data = await UsuariosServiceAPI.buscarTodos();
      
      // Mapear dados para o tipo Usuario da UI
      const usuariosFormatados = data.map((u: UsuarioType) => ({
        id: u.id,
        nome: u.nome,
        login: u.login,
        email: u.email || '',
        perfil: u.perfil_id, // Mantém o perfil_id como perfil para compatibilidade
        perfil_id: u.perfil_id, // Adiciona perfil_id separadamente
        filiais: u.filiais_permitidas || [],
        telefone: u.telefone,
        cnpj: u.cnpj,
        regiao: u.regiao,
        eh_transportadora: u.eh_transportadora,
        primeiro_acesso: u.primeiro_acesso,
        foto_perfil: u.foto_perfil,
        criado_em: u.criado_em,
        bloqueado: false
      }));
      
      // Verificar bloqueio de cada usuário (apenas para admins)
      if (user?.perfil === 'admin') {
        const usuariosComBloqueio = await Promise.all(
          usuariosFormatados.map(async (usuario) => {
            try {
              const bloqueioInfo = await UsuariosServiceAPI.verificarBloqueio(usuario.id);
              return {
                ...usuario,
                bloqueado: bloqueioInfo.bloqueado,
                tipoBloqueio: bloqueioInfo.tipo,
                motivoBloqueio: bloqueioInfo.motivo
              };
            } catch {
              return {
                ...usuario,
                bloqueado: false
              };
            }
          })
        );
        setUsuarios(usuariosComBloqueio);
      } else {
        setUsuarios(usuariosFormatados);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setLoading(false);
    }
  };

  const carregarSolicitacoes = async () => {
    try {
      const response = await SolicitacoesAcessoServiceAPI.buscarPendentes();
      
      if (response.success && response.data) {
        setSolicitacoes(response.data);
      } else {
        setSolicitacoes([]);
      }
    } catch (error) {
      console.error('Erro ao carregar solicitações:', error);
      setSolicitacoes([]);
    }
  };

  const carregarPerfis = async () => {
    try {
      const { data, error } = await PerfisService.buscarTodos();
      
      if (data) {
        // Filtrar apenas perfis com estrutura JSON de permissões (sistema novo)
        // Perfis legados não têm o campo permissoes como objeto JSON
        const perfisFiltrados = data.filter(p => {
          // Verificar se tem a estrutura de permissões JSON esperada
          return p.permissoes && typeof p.permissoes === 'object' && 
                 (p.permissoes.cargas || p.permissoes.gestao_agendamento || p.permissoes.usuarios);
        });
        setPerfis(perfisFiltrados);
      } else {
        setPerfis([]);
      }
    } catch (error) {
      console.error('Erro ao carregar perfis:', error);
      setPerfis([]);
    }
  };

  const carregarFiliais = async () => {
    try {
      const data = await FiliaisServiceAPI.buscarTodas();
      setFiliais(data);
    } catch (error) {
      console.error('Erro ao carregar filiais:', error);
      setFiliais([]);
    }
  };

  // Função auxiliar para buscar nome do perfil por ID
  const getNomePerfil = (perfilId: string) => {
    const perfil = perfis.find(p => p.id === perfilId);
    return perfil?.nome || perfilId;
  };

  // Função auxiliar para buscar nomes das filiais por IDs
  const getNomesFiliais = (filiaisIds: string[]) => {
    if (!filiaisIds || filiaisIds.length === 0) return 'Nenhuma';
    const nomes = filiaisIds
      .map(id => filiais.find(f => f.id === id)?.nome || id)
      .join(', ');
    return nomes;
  };

  const aplicarFiltros = () => {
    let filtrados = usuarios;
    // Filtrar pela filial selecionada no seletor (sempre aplicar, não apenas para não-admin/gerente)
    if (filialAtual) {
      filtrados = filtrados.filter(u => {
        // Se não tem filiais definidas, não mostrar (exceto admin/gerente)
        if (!u.filiais || u.filiais.length === 0) return user?.perfil === 'admin' || user?.perfil === 'gerente';
        // Se tem "TODAS AS FILIAIS", mostrar em qualquer filial
        if (u.filiais.includes('TODAS AS FILIAIS')) return true;
        
        // Normalizar filial atual para código
        let filialCodigo = filialAtual;
        if (filialAtual.includes('Filial 1') || filialAtual.includes('UP Distribuidora')) filialCodigo = 'FILIAL_1';
        else if (filialAtual.includes('Filial 2') || filialAtual.includes('Aleplast')) filialCodigo = 'FILIAL_2';
        else if (filialAtual.includes('Filial 3') || filialAtual.includes('Alepack')) filialCodigo = 'FILIAL_3';
        
        // Verificar se o usuário tem acesso à filial selecionada
        const temAcessoAFilial = u.filiais.some(f => {
          // Normalizar filial do usuário para código
          let usuarioCodigo = f;
          if (f.includes('Filial 1') || f.includes('UP Distribuidora')) usuarioCodigo = 'FILIAL_1';
          else if (f.includes('Filial 2') || f.includes('Aleplast')) usuarioCodigo = 'FILIAL_2';
          else if (f.includes('Filial 3') || f.includes('Alepack')) usuarioCodigo = 'FILIAL_3';
          
          // Verificar correspondência
          return usuarioCodigo === filialCodigo;
        });
        
        return temAcessoAFilial;
      });
    }

    if (filtroNome) {
      filtrados = filtrados.filter(u => 
        u.nome.toLowerCase().includes(filtroNome.toLowerCase()) ||
        u.login.toLowerCase().includes(filtroNome.toLowerCase())
      );
    }

    if (filtroPerfil) {
      filtrados = filtrados.filter(u => u.perfil === filtroPerfil);
    }

    setUsuariosFiltrados(filtrados);
  };

  const handleEditar = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setPerfilUsuarioModal(usuario.perfil);
    setModalUsuarioOpen(true);
  };

  const handleAlterarSenha = (usuario: Usuario) => {
    setUsuarioSelecionado(usuario);
    setModalSenhaOpen(true);
  };

  const handleResetarSenha = async (usuario: Usuario) => {
    try {
      const senhaPadrao = 'Up123456@Up123';
      await UsuariosServiceAPI.resetarSenha(usuario.id, senhaPadrao);

      alert(`Senha resetada com sucesso! A nova senha é: ${senhaPadrao}\nO usuário precisará redefinir no próximo login.`);
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      alert('Erro ao resetar senha');
    }
  };

  const handleDesbloquear = async (usuario: Usuario) => {
    try {
      await UsuariosServiceAPI.desbloquearConta(usuario.id);
      alert('Conta desbloqueada com sucesso!');
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao desbloquear conta:', error);
      alert('Erro ao desbloquear conta');
    }
  };

  const handleAprovar = async () => {
    if (!solicitacaoSelecionada || !user) return;

    try {
      // Usar o perfil da solicitação (fornecedor) ou permitir que o admin escolha
      const perfilAprovacao = solicitacaoSelecionada.perfil_id || 'fornecedor';
      
      const response = await SolicitacoesAcessoServiceAPI.aprovar(
        solicitacaoSelecionada.id,
        {
          perfil_id: perfilAprovacao,
          filiais_permitidas: solicitacaoSelecionada.filiais_permitidas || (solicitacaoSelecionada.filial_pretendida ? [solicitacaoSelecionada.filial_pretendida] : ['FILIAL_1'])
        }
      );

      if (!response.success) {
        alert('Erro ao aprovar solicitação: ' + response.message);
        return;
      }

      // Notificar o usuário sobre a aprovação
      try {
        // Primeiro buscar o ID do usuário criado
        const todosUsuarios = await UsuariosServiceAPI.buscarTodos();
        const usuariosCriados = todosUsuarios?.filter((u: any) => u.email === solicitacaoSelecionada.email);
        if (usuariosCriados && usuariosCriados.length > 0) {
          await NotificacoesService.notificarSolicitacaoAcessoAprovada(
            usuariosCriados[0].id,
            solicitacaoSelecionada.id,
            solicitacaoSelecionada.nome,
            solicitacaoSelecionada.email
          );
        }
      } catch (notificationError) {
        console.error('Erro ao enviar notificação de aprovação:', notificationError);
        // Não falhar a aprovação se a notificação falhar
      }

      alert('Solicitação aprovada com sucesso!');
      setModalAprovarOpen(false);
      setSolicitacaoSelecionada(null);
      carregarSolicitacoes();
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao aprovar solicitação:', error);
      alert('Erro ao aprovar solicitação');
    }
  };

  const handleReprovar = async () => {
    if (!solicitacaoSelecionada || !user) return;

    if (!motivoReprovacao.trim()) {
      alert('Por favor, informe o motivo da reprovação');
      return;
    }

    try {
      const response = await SolicitacoesAcessoServiceAPI.reprovar(
        solicitacaoSelecionada.id,
        { motivo_reprovacao: motivoReprovacao }
      );

      if (!response.success) {
        console.error('Erro detalhado ao reprovar:', response.message);
        alert('Erro ao reprovar solicitação: ' + response.message);
        return;
      }

      // Notificar o usuário sobre a reprovação
      try {
        // Para reprovação, notificação vai para o email da solicitação (usuário ainda não criado)
        // TODO: Implementar sistema de notificação por email para usuários não criados
      } catch (notificationError) {
        // Não falhar a reprovação se a notificação falhar
      }

      alert('Solicitação reprovada com sucesso!');
      setModalReprovarOpen(false);
      setSolicitacaoSelecionada(null);
      setMotivoReprovacao('');
      carregarSolicitacoes();
    } catch (error) {
      console.error('Erro ao reprovar solicitação:', error);
      const errorMessage = error instanceof Error ? error.message : JSON.stringify(error);
      alert('Erro ao reprovar solicitação: ' + errorMessage);
    }
  };

  // Contar usuários por perfil (considerando apenas filial atual)
  const contarPorPerfil = (perfil: string) => {
    const usuariosDaFilial = filialAtual 
      ? usuarios.filter(u => u.filiais.includes(filialAtual))
      : usuarios;
    return usuariosDaFilial.filter(u => u.perfil === perfil).length;
  };

  const contadores = [
    { perfil: 'admin', label: 'Administradores', icone: '👑', cor: 'bg-blue-500', count: contarPorPerfil('admin') },
    { perfil: 'compras', label: 'Compras', icone: '🛒', cor: 'bg-green-500', count: contarPorPerfil('compras') },
    { perfil: 'CM', label: 'CM', icone: '📋', cor: 'bg-purple-500', count: contarPorPerfil('CM') },
    { perfil: 'supervisor', label: 'Supervisores', icone: '👥', cor: 'bg-orange-500', count: contarPorPerfil('supervisor') },
    { perfil: 'gestor', label: 'Gestores', icone: '📊', cor: 'bg-pink-500', count: contarPorPerfil('gestor') },
    { perfil: 'fornecedor', label: 'Fornecedores', icone: '🏢', cor: 'bg-teal-500', count: contarPorPerfil('fornecedor') },
  ];

  // Forçar re-render quando filial mudar para atualizar contadores
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    setForceUpdate(prev => prev + 1);
  }, [filialAtual]);

  const handleExcluirUsuario = async (usuario: Usuario) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${usuario.nome}"? Esta ação não pode ser desfeita.`)) {
      return;
    }

    try {
      await UsuariosServiceAPI.deletar(usuario.id);

      alert('Usuário excluído com sucesso!');
      carregarUsuarios();
    } catch (error) {
      console.error('Erro ao excluir usuário:', error);
      alert('Erro ao excluir usuário. Tente novamente.');
    }
  };

  const handleExcluirPerfil = async (perfil: Perfil) => {
    if (!window.confirm(`Tem certeza que deseja excluir o perfil "${perfil.nome}"?`)) {
      return;
    }

    try {
      const { error } = await PerfisService.excluir(perfil.id);
      
      if (error) {
        alert('Erro ao excluir perfil: ' + error);
        return;
      }

      await carregarPerfis();
      alert('Perfil excluído com sucesso!');
    } catch (error) {
      console.error('Erro ao excluir perfil:', error);
      alert('Erro ao excluir perfil. Tente novamente.');
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Usuários</h1>
        <p className="text-gray-600 mt-1">Gerenciamento de usuários e perfis</p>
      </div>

      {/* Abas */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setAbaAtiva('usuarios')}
          className={`px-4 py-2 font-medium ${
            abaAtiva === 'usuarios'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Usuários ({usuarios.length})
        </button>
        {podeAprovar && (
          <button
            onClick={() => setAbaAtiva('aprovacoes')}
            className={`px-4 py-2 font-medium ${
              abaAtiva === 'aprovacoes'
                ? 'border-b-2 border-blue-500 text-blue-600'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Aprovações ({solicitacoes.length})
          </button>
        )}
        {podeGerenciarPerfis && (
          <button
            onClick={() => setAbaAtiva('perfis')}
            className={`px-4 py-2 font-medium ${
              abaAtiva === 'perfis'
              ? 'border-b-2 border-blue-500 text-blue-600'
              : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Perfis e Permissões
          </button>
        )}
      </div>

      {/* Conteúdo da Aba Usuários */}
      {abaAtiva === 'usuarios' && (
        <>
          {/* Cards de Contadores */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {contadores.map((item) => (
              <div
                key={item.perfil}
                className={`${item.cor} rounded-xl p-6 text-white shadow-lg hover:shadow-xl transition-shadow cursor-pointer`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm opacity-90">{item.label}</p>
                    <p className="text-4xl font-bold mt-2">{item.count}</p>
                  </div>
                  <div className="text-5xl opacity-80">{item.icone}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Cabeçalho com botão de criar */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Usuários Cadastrados</h2>
              <p className="text-gray-600 mt-1">Gerencie os usuários do sistema</p>
            </div>
            <button
              onClick={() => {
                setUsuarioSelecionado(null);
                setPerfilUsuarioModal('');
                setModalUsuarioOpen(true);
              }}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 flex items-center gap-2"
            >
              <span>+</span> Novo Usuário
            </button>
          </div>

          {/* Filtros */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Filtros</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Buscar por nome ou login</label>
                <input
                  type="text"
                  value={filtroNome}
                  onChange={(e) => setFiltroNome(e.target.value)}
                  placeholder="Digite para buscar..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filtrar por perfil</label>
                <select
                  value={filtroPerfil}
                  onChange={(e) => setFiltroPerfil(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Todos os perfis</option>
                  {perfis.map(perfil => (
                    <option key={perfil.id} value={perfil.nome}>
                      {perfil.nome}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Lista de Usuários */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-800">
                Lista de Usuários {filtroNome || filtroPerfil ? `(Filtrado: ${usuariosFiltrados.length})` : `(${usuariosFiltrados.length})`}
              </h2>
            </div>
            <div className="p-6">
              {loading ? (
                <p className="text-center text-gray-500">Carregando...</p>
              ) : usuariosFiltrados.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum usuário encontrado</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {usuariosFiltrados.map((usuario) => (
                    <div key={usuario.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      {/* Header com foto/inicial e nome */}
                      <div className="flex items-start gap-4 mb-4">
                        {usuario.foto_perfil ? (
                          <img
                            src={usuario.foto_perfil}
                            alt={usuario.nome}
                            className="w-12 h-12 rounded-full object-cover"
                            onError={(e) => {
                              // Se a foto falhar ao carregar, mostra a inicial
                              (e.target as HTMLImageElement).style.display = 'none';
                              (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                            }}
                          />
                        ) : null}
                        {!usuario.foto_perfil && (
                          <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white text-xl font-bold">
                            {usuario.nome.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-800">{usuario.nome}</h3>
                          <p className="text-sm text-gray-500">@{usuario.login}</p>
                          <p className="text-xs text-blue-600 font-medium mt-1">
                            Perfil: {getNomePerfil(usuario.perfil)}
                          </p>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="mb-4 flex items-center gap-2">
                        {usuario.bloqueado ? (
                          <div className="flex items-center gap-2">
                            <span className="text-red-500">🔒 Bloqueado</span>
                            {usuario.tipoBloqueio === 'permanente' && (
                              <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">Permanente</span>
                            )}
                            {usuario.tipoBloqueio === 'temporario' && (
                              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">Temporário</span>
                            )}
                          </div>
                        ) : (
                          <span className="text-green-500">✅ Ativo</span>
                        )}
                      </div>

                      {/* Informações detalhadas */}
                      <div className="space-y-2 text-sm">
                        <div className="flex">
                          <span className="text-gray-500 w-20">Perfil:</span>
                          <span className="text-gray-800 font-medium">{getNomePerfil(usuario.perfil)}</span>
                        </div>
                        <div className="flex">
                          <span className="text-gray-500 w-20">E-mail:</span>
                          <span className="text-gray-800">{usuario.email}</span>
                        </div>
                        {usuario.cnpj && (
                          <div className="flex">
                            <span className="text-gray-500 w-20">CNPJ:</span>
                            <span className="text-gray-800">{usuario.cnpj}</span>
                          </div>
                        )}
                        <div className="flex">
                          <span className="text-gray-500 w-20">Filiais:</span>
                          <span className="text-gray-800">{getNomesFiliais(usuario.filiais)}</span>
                        </div>
                        <div className="flex">
                          <span className="text-gray-500 w-20">Cadastro:</span>
                          <span className="text-gray-800">{new Date(usuario.criado_em).toLocaleDateString('pt-BR')}</span>
                        </div>
                      </div>

                      {/* Botões de ação */}
                      <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-gray-100">
                        <button
                          onClick={() => handleEditar(usuario)}
                          className="text-blue-500 hover:text-blue-700 text-lg"
                          title="Editar"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleAlterarSenha(usuario)}
                          className="text-yellow-500 hover:text-yellow-700 text-lg"
                          title="Alterar Senha"
                        >
                          🔑
                        </button>
                        <button
                          onClick={() => handleResetarSenha(usuario)}
                          className="text-red-500 hover:text-red-700 text-lg"
                          title="Resetar Senha"
                        >
                          🔄
                        </button>
                        {user?.perfil === 'admin' && usuario.bloqueado && (
                          <button
                            onClick={() => handleDesbloquear(usuario)}
                            className="text-green-600 hover:text-green-800 text-lg"
                            title="Desbloquear Conta"
                          >
                            🔓
                          </button>
                        )}
                        <button
                          onClick={() => handleExcluirUsuario(usuario)}
                          className="text-red-600 hover:text-red-800 text-lg"
                          title="Excluir Usuário"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Conteúdo da Aba Aprovações */}
      {abaAtiva === 'aprovacoes' && podeAprovar && (
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">
              Solicitações de Acesso Pendentes ({solicitacoes.length})
            </h2>
          </div>
          <div className="p-6">
            {loading ? (
              <p className="text-center text-gray-500">Carregando...</p>
            ) : solicitacoes.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhuma solicitação pendente</p>
            ) : (
              <div className="space-y-4">
                {solicitacoes.map((solicitacao) => (
                  <div key={solicitacao.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-800">{solicitacao.nome}</h3>
                        <p className="text-sm text-gray-500">@{solicitacao.login}</p>
                        <p className="text-sm text-gray-600 mt-2">{solicitacao.email}</p>
                        {solicitacao.cnpj && (
                          <p className="text-sm text-gray-500 mt-1">
                            CNPJ: <span className="font-medium">{solicitacao.cnpj}</span>
                          </p>
                        )}
                        {solicitacao.filial_pretendida && (
                          <p className="text-sm text-gray-500 mt-1">
                            Filial: <span className="font-medium">{solicitacao.filial_pretendida}</span>
                          </p>
                        )}
                        {solicitacao.regiao && (
                          <p className="text-sm text-gray-500 mt-1">
                            Região: <span className="font-medium">{solicitacao.regiao}</span>
                          </p>
                        )}
                        <p className="text-sm text-gray-500 mt-1">
                          Perfil: <span className="font-medium">{solicitacao.perfil_id}</span>
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          Motivo: <span className="font-medium">{solicitacao.motivo}</span>
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          Solicitado em: {new Date(solicitacao.data_solicitacao).toLocaleString('pt-BR')}
                        </p>

                        {(solicitacao.ja_e_fornecedor !== undefined || solicitacao.meio_comunicacao) && (
                          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs font-semibold text-blue-900 mb-2">📋 Questionário</p>
                            <div className="grid grid-cols-2 gap-1 text-xs text-gray-700">
                              <span>É fornecedor: <strong>{solicitacao.ja_e_fornecedor ? 'Sim' : 'Não'}</strong></span>
                              <span>Realizou venda: <strong>{solicitacao.realizou_venda ? 'Sim' : 'Não'}</strong></span>
                              <span>Tem carga p/ agendar: <strong>{solicitacao.tem_carga_agendar ? 'Sim' : 'Não'}</strong></span>
                              <span>Visita presencial: <strong>{solicitacao.realizou_visita_presencial ? 'Sim' : 'Não'}</strong></span>
                              <span>Deseja visita: <strong>{solicitacao.deseja_visita ? 'Sim' : 'Não'}</strong></span>
                              <span>Negócio fechado: <strong>{solicitacao.negocio_fechado ? 'Sim' : 'Não'}</strong></span>
                            </div>
                            {solicitacao.meio_comunicacao && (
                              <p className="text-xs text-gray-700 mt-1">Comunicação: <strong>{solicitacao.meio_comunicacao}</strong></p>
                            )}
                            {solicitacao.observacoes_questionario && (
                              <p className="text-xs text-gray-600 mt-1 italic">Obs: {solicitacao.observacoes_questionario}</p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSolicitacaoSelecionada(solicitacao);
                            setModalAprovarOpen(true);
                          }}
                          className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors"
                        >
                          Aprovar
                        </button>
                        <button
                          onClick={() => {
                            setSolicitacaoSelecionada(solicitacao);
                            setModalReprovarOpen(true);
                          }}
                          className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors"
                        >
                          Reprovar
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modals */}
      <EditarUsuarioModal
        isOpen={modalEditOpen}
        onClose={() => {
          setModalEditOpen(false);
          setUsuarioSelecionado(null);
        }}
        usuario={usuarioSelecionado as any}
        onSave={carregarUsuarios}
      />

      <AlterarSenhaModal
        isOpen={modalSenhaOpen}
        onClose={() => {
          setModalSenhaOpen(false);
          setUsuarioSelecionado(null);
        }}
        usuarioId={usuarioSelecionado?.id || ''}
        usuarioNome={usuarioSelecionado?.nome || ''}
      />

      <Modal
        isOpen={modalAprovarOpen}
        onClose={() => {
          setModalAprovarOpen(false);
          setSolicitacaoSelecionada(null);
        }}
        title="Aprovar Solicitação de Acesso"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Tem certeza que deseja aprovar a solicitação de acesso de <strong>{solicitacaoSelecionada?.nome}</strong>?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => {
                setModalAprovarOpen(false);
                setSolicitacaoSelecionada(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleAprovar}
              className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-600"
            >
              Confirmar Aprovação
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={modalReprovarOpen}
        onClose={() => {
          setModalReprovarOpen(false);
          setSolicitacaoSelecionada(null);
          setMotivoReprovacao('');
        }}
        title="Reprovar Solicitação de Acesso"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Tem certeza que deseja reprovar a solicitação de acesso de <strong>{solicitacaoSelecionada?.nome}</strong>?
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Motivo da reprovação</label>
            <textarea
              value={motivoReprovacao}
              onChange={(e) => setMotivoReprovacao(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Informe o motivo..."
              required
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => {
                setModalReprovarOpen(false);
                setSolicitacaoSelecionada(null);
                setMotivoReprovacao('');
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              onClick={handleReprovar}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
            >
              Confirmar Reprovação
            </button>
          </div>
        </div>
      </Modal>

      {/* Conteúdo da Aba Perfis */}
      {abaAtiva === 'perfis' && podeGerenciarPerfis && (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">Perfis e Permissões</h2>
              <p className="text-gray-600 mt-1">Gerencie os perfis de acesso e suas permissões</p>
            </div>
            <button
              onClick={() => {
                setPerfilSelecionado(null);
                setModalPerfilOpen(true);
              }}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 flex items-center gap-2"
            >
              <span>+</span> Novo Perfil
            </button>
          </div>

          {/* Lista de Perfis */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Perfil
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Descrição
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Usuários
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {perfis.map((perfil) => (
                  <tr key={perfil.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-blue-100">
                          <span className="text-blue-600 font-medium">
                            {perfil.nome.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{perfil.nome}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{perfil.descricao || 'Sem descrição'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        perfil.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {perfil.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {usuarios.filter(u => u.perfil === perfil.nome).length}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setPerfilSelecionado(perfil);
                          setModalPerfilOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 mr-3"
                      >
                        Editar
                      </button>
                      {perfil.nome !== 'fornecedor' && perfil.nome !== 'admin' && (
                        <button
                          onClick={() => handleExcluirPerfil(perfil)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Excluir
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PerfilPermissoesModal
        isOpen={modalPerfilOpen}
        onClose={() => setModalPerfilOpen(false)}
        perfil={perfilSelecionado}
        onSave={() => {
          carregarPerfis();
          setModalPerfilOpen(false);
          setPerfilSelecionado(null);
        }}
      />

      {/* Modal de Usuário */}
      <Modal
        isOpen={modalUsuarioOpen}
        onClose={() => {
          setModalUsuarioOpen(false);
          setUsuarioSelecionado(null);
          setPerfilUsuarioModal('');
        }}
        title={usuarioSelecionado ? "Editar Usuário" : "Novo Usuário"}
        size="lg"
      >
        <form onSubmit={handleSalvarUsuario} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nome *</label>
              <input
                type="text"
                name="nome"
                defaultValue={usuarioSelecionado?.nome || ''}
                placeholder="Nome completo"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Login *</label>
              <input
                type="text"
                name="login"
                defaultValue={usuarioSelecionado?.login || ''}
                placeholder="Nome de usuário"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
              <input
                type="email"
                name="email"
                defaultValue={usuarioSelecionado?.email || ''}
                placeholder="email@exemplo.com"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Perfil *</label>
              <select
                name="perfil"
                defaultValue={usuarioSelecionado?.perfil || ''}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                onChange={(e) => setPerfilUsuarioModal(e.target.value)}
              >
                <option value="">Selecione um perfil</option>
                {perfis.map(perfil => (
                  <option key={perfil.id} value={perfil.id}>
                    {perfil.nome}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
              <input
                type="tel"
                name="telefone"
                defaultValue={usuarioSelecionado?.telefone || ''}
                placeholder="(00) 00000-0000"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                CNPJ {perfis.find(p => p.id === perfilUsuarioModal)?.nome.toLowerCase() === 'fornecedor' ? '*' : '(opcional para usuários internos)'}
              </label>
              <input
                type="text"
                name="cnpj"
                defaultValue={usuarioSelecionado?.cnpj || ''}
                placeholder="00.000.000/0000-00"
                required={perfis.find(p => p.id === perfilUsuarioModal)?.nome.toLowerCase() === 'fornecedor'}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Filiais Permitidas *</label>
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="filial_FILIAL_1"
                    defaultChecked={usuarioSelecionado?.filiais?.includes('FILIAL_1') || true}
                    className="mr-2"
                  />
                  <label htmlFor="filial_FILIAL_1" className="text-sm">Filial 1 - UP Distribuidora</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="filial_FILIAL_2"
                    defaultChecked={usuarioSelecionado?.filiais?.includes('FILIAL_2') || false}
                    className="mr-2"
                  />
                  <label htmlFor="filial_FILIAL_2" className="text-sm">Filial 2 - Aleplast</label>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="filial_FILIAL_3"
                    defaultChecked={usuarioSelecionado?.filiais?.includes('FILIAL_3') || false}
                    className="mr-2"
                  />
                  <label htmlFor="filial_FILIAL_3" className="text-sm">Filial 3 - Alepack</label>
                </div>
              </div>
            </div>
          </div>
          
          {!usuarioSelecionado && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Atenção:</strong> A senha padrão para novos usuários será <code>Up123456@Up123</code>. 
                O usuário precisará alterá-la no primeiro acesso.
              </p>
            </div>
          )}
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setModalUsuarioOpen(false);
                setUsuarioSelecionado(null);
              }}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            >
              {usuarioSelecionado ? 'Atualizar' : 'Criar'} Usuário
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Usuarios;
